<template>
  <div class="review-form-page">
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h1 class="h3 mb-1">
          {{ isEditing ? "Edit Review" : "Create New Review" }}
        </h1>
        <p class="text-muted mb-0">
          {{
            isEditing ? "Update review details" : "Add a customer testimonial"
          }}
        </p>
      </div>
      <router-link to="/admin/reviews" class="btn btn-outline-secondary">
        <i class="fas fa-arrow-left me-2"></i>
        Back to Reviews
      </router-link>
    </div>

    <!-- Review Form -->
    <div class="row">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Review Details</h5>
          </div>
          <div class="card-body">
            <form @submit.prevent="handleSubmit">
              <!-- Reviewer Information -->
              <div class="row mb-3">
                <div class="col-md-6">
                  <label for="reviewerName" class="form-label"
                    >Reviewer Name *</label
                  >
                  <input
                    type="text"
                    class="form-control"
                    id="reviewerName"
                    v-model="form.reviewerName"
                    :class="{ 'is-invalid': errors.reviewerName }"
                    placeholder="Enter reviewer's name"
                    required
                  />
                  <div v-if="errors.reviewerName" class="invalid-feedback">
                    {{ errors.reviewerName }}
                  </div>
                </div>

                <div class="col-md-6">
                  <label for="rating" class="form-label">Rating *</label>
                  <div class="rating-input">
                    <div
                      v-for="n in 5"
                      :key="n"
                      class="star"
                      :class="{
                        active: n <= form.rating,
                        hoverable: n <= hoverRating,
                      }"
                      @click="setRating(n)"
                      @mouseenter="hoverRating = n"
                      @mouseleave="hoverRating = 0"
                    >
                      <i class="fas fa-star"></i>
                    </div>
                    <span class="rating-text ms-2">{{ form.rating }} / 5</span>
                  </div>
                  <div v-if="errors.rating" class="invalid-feedback d-block">
                    {{ errors.rating }}
                  </div>
                </div>
              </div>

              <!-- Review Text -->
              <div class="mb-3">
                <label for="reviewText" class="form-label">Review Text *</label>
                <textarea
                  class="form-control"
                  id="reviewText"
                  v-model="form.reviewText"
                  :class="{ 'is-invalid': errors.reviewText }"
                  rows="5"
                  placeholder="Enter the customer's review"
                  required
                ></textarea>
                <div v-if="errors.reviewText" class="invalid-feedback">
                  {{ errors.reviewText }}
                </div>
                <div class="form-text">
                  Share what the customer loved about their experience
                </div>
              </div>

              <!-- Images Upload -->
              <div class="mb-4">
                <label class="form-label">Images (Optional)</label>
                <FileUpload
                  :multiple="true"
                  :max-files="3"
                  accept="image/*"
                  @files-selected="handleImagesSelected"
                  :existing-files="existingImages"
                  :trip-id="id"
                />
                <div class="form-text">
                  <strong>Images:</strong> Upload up to 3 images to showcase the
                  customer's experience.
                  <br />
                  <small class="text-muted">
                    <strong>Supported formats:</strong> JPG, PNG, GIF, WebP,
                    HEIC
                    <br />
                    <strong>File size limits:</strong> 50MB max per image
                  </small>
                </div>
              </div>

              <!-- Video Upload -->
              <div class="mb-4">
                <label class="form-label">Video (Optional)</label>
                <FileUpload
                  :multiple="false"
                  :max-files="1"
                  accept="video/*"
                  @files-selected="handleVideoSelected"
                  :existing-files="existingVideo ? [existingVideo] : []"
                  :trip-id="id"
                />
                <div class="form-text">
                  <strong>Video:</strong> Upload 1 video to showcase the
                  customer's experience.
                  <br />
                  <small class="text-muted">
                    <strong>Supported formats:</strong> MP4, MOV, AVI, WMV, FLV,
                    WebM
                    <br />
                    <strong>File size limits:</strong> 100MB max
                    <br />
                    <strong>Note:</strong> Cannot upload both images and video.
                    Choose either up to 3 images OR 1 video.
                  </small>
                </div>
              </div>

              <!-- Error Display -->
              <div v-if="error" class="alert alert-danger" role="alert">
                <i class="fas fa-exclamation-triangle me-2"></i>
                {{ error }}
              </div>

              <!-- Form Actions -->
              <div class="d-flex justify-content-end gap-2">
                <router-link to="/admin/reviews" class="btn btn-secondary">
                  Cancel
                </router-link>
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="isLoading"
                >
                  <span
                    v-if="isLoading"
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span class="visually-hidden">Loading...</span>
                  </span>
                  <i
                    v-else
                    :class="isEditing ? 'fas fa-save' : 'fas fa-plus'"
                    class="me-2"
                  ></i>
                  {{
                    isLoading
                      ? "Saving..."
                      : isEditing
                      ? "Update Review"
                      : "Create Review"
                  }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Preview Panel -->
      <div class="col-lg-4">
        <div class="card">
          <div class="card-header">
            <h6 class="mb-0">Preview</h6>
          </div>
          <div class="card-body">
            <div class="review-preview">
              <!-- Reviewer Info -->
              <div
                class="d-flex justify-content-between align-items-start mb-3"
              >
                <div>
                  <h6 class="mb-1">
                    {{ form.reviewerName || "Reviewer Name" }}
                  </h6>
                  <div class="rating">
                    <i
                      v-for="n in 5"
                      :key="n"
                      :class="
                        n <= form.rating
                          ? 'fas fa-star text-warning'
                          : 'far fa-star text-muted'
                      "
                    ></i>
                  </div>
                </div>
              </div>

              <!-- Review Text -->
              <p class="review-text">
                {{ form.reviewText || "Customer review will appear here..." }}
              </p>

              <!-- Images Preview -->
              <div v-if="previewImages.length > 0" class="media-preview">
                <div class="media-grid">
                  <div
                    v-for="(image, index) in previewImages.slice(0, 3)"
                    :key="index"
                    class="media-item"
                  >
                    <img
                      :src="getMediaUrl(image)"
                      :alt="`Preview ${index + 1}`"
                      class="media-thumbnail"
                    />
                  </div>

                  <div v-if="previewImages.length > 3" class="media-more">
                    +{{ previewImages.length - 3 }}
                  </div>
                </div>

                <!-- Images summary -->
                <div class="media-summary mt-2">
                  <small class="text-muted">
                    <i class="fas fa-images me-1"></i>
                    {{ previewImages.length }} image{{
                      previewImages.length !== 1 ? "s" : ""
                    }}
                    - Review text will be hidden in display
                  </small>
                </div>
              </div>

              <!-- Video Preview -->
              <div v-if="previewVideo.length > 0" class="media-preview">
                <div class="media-grid">
                  <div class="media-item">
                    <video
                      :src="getMediaUrl(previewVideo[0])"
                      class="media-thumbnail"
                      muted
                    ></video>
                    <!-- Video indicator -->
                    <div class="video-indicator">
                      <i class="fas fa-play"></i>
                    </div>
                  </div>
                </div>

                <!-- Video summary -->
                <div class="media-summary mt-2">
                  <small class="text-muted">
                    <i class="fas fa-video me-1"></i>
                    1 video - Review text will be hidden in display
                  </small>
                </div>
              </div>

              <!-- Date -->
              <div class="review-date mt-3 pt-3 border-top">
                <small class="text-muted">
                  <i class="fas fa-calendar me-1"></i>
                  {{ new Date().toLocaleDateString() }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Swal from "sweetalert2";
import FileUpload from "@/components/FileUpload.vue";

export default {
  name: "ReviewForm",
  components: {
    FileUpload,
  },
  props: {
    id: String,
  },
  data() {
    return {
      form: {
        reviewerName: "",
        reviewText: "",
        rating: 5,
        images: [],
        video: null,
      },
      errors: {},
      error: null,
      isLoading: false,
      hoverRating: 0,
      existingImages: [],
      existingVideo: null,
    };
  },
  computed: {
    ...mapGetters("reviews", ["currentReview"]),

    isEditing() {
      return !!this.id;
    },

    previewImages() {
      return [...this.existingImages, ...this.form.images];
    },

    previewVideo() {
      return this.existingVideo
        ? [this.existingVideo]
        : this.form.video
        ? [this.form.video]
        : [];
    },
  },
  methods: {
    ...mapActions("reviews", [
      "createReview",
      "updateReview",
      "fetchReviewById",
      "clearCurrentReview",
    ]),

    validateForm() {
      this.errors = {};

      if (!this.form.reviewerName.trim()) {
        this.errors.reviewerName = "Reviewer name is required";
      }

      if (!this.form.reviewText.trim()) {
        this.errors.reviewText = "Review text is required";
      }

      if (!this.form.rating || this.form.rating < 1 || this.form.rating > 5) {
        this.errors.rating = "Please select a rating between 1 and 5";
      }

      return Object.keys(this.errors).length === 0;
    },

    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        // Combine existing images (that weren't removed) with new images
        const allImages = [...this.existingImages, ...this.form.images];

        // Determine final video:
        // - If existingVideo exists and wasn't removed, use it
        // - If new video was uploaded, use form.video
        // - If video was removed (both are null), set to null explicitly
        let finalVideo = null;
        if (this.existingVideo) {
          // Existing video is still there (wasn't removed)
          finalVideo = this.existingVideo;
        } else if (this.form.video) {
          // New video was uploaded
          finalVideo = this.form.video;
        }
        // Otherwise finalVideo remains null (video was removed)

        // Prepare review data with combined images and video
        const reviewData = {
          ...this.form,
          images: allImages.length > 0 ? allImages : [],
          video: finalVideo, // Explicitly set to null if removed
        };

        if (this.isEditing) {
          await this.updateReview({
            id: this.id,
            reviewData: reviewData,
          });
        } else {
          await this.createReview(reviewData);
        }

        // Show success message
        await Swal.fire({
          title: "Success!",
          text: `Review ${this.isEditing ? "updated" : "created"} successfully`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        // Redirect to reviews list
        this.$router.push("/admin/reviews");
      } catch (error) {
        this.error = error.message;

        Swal.fire({
          title: "Error",
          text: error.message,
          icon: "error",
          confirmButtonColor: "#2c5aa0",
        });
      } finally {
        this.isLoading = false;
      }
    },

    setRating(rating) {
      this.form.rating = rating;
    },

    handleImagesSelected(files) {
      // FileUpload component emits all files (existing + new) as URLs
      // Separate existing images from new images
      const allImageUrls = files.filter((file) => typeof file === "string");

      // Find which images are from existingImages (already in database)
      const existingUrls = this.existingImages.filter(url =>
        allImageUrls.includes(url)
      );

      // Find new images (not in existingImages)
      const newUrls = allImageUrls.filter(url =>
        !this.existingImages.includes(url)
      );

      // Update existingImages to reflect removals
      this.existingImages = existingUrls;

      // Set form.images to only new images (existing will be handled separately)
      this.form.images = newUrls;
    },

    handleVideoSelected(files) {
      // FileUpload component emits all files (existing + new) as URLs
      const urlFiles = files.filter((file) => typeof file === "string");

      if (urlFiles.length > 0) {
        // Check if it's the existing video or a new one
        const videoUrl = urlFiles[0];
        if (this.existingVideo === videoUrl) {
          // It's the existing video, keep it in existingVideo
          this.form.video = null; // No new video
        } else {
          // It's a new video
          this.existingVideo = null; // Clear existing video
          this.form.video = videoUrl;
        }
      } else {
        // Video was removed
        this.existingVideo = null;
        this.form.video = null;
      }
    },

    isImage(file) {
      if (typeof file === "string") {
        return /\.(jpg|jpeg|png|gif|webp)$/i.test(file);
      }
      return file.type && file.type.startsWith("image/");
    },

    isVideo(file) {
      if (typeof file === "string") {
        return /\.(mp4|avi|mov|wmv|flv|webm)$/i.test(file);
      }
      return file.type && file.type.startsWith("video/");
    },

    getMediaUrl(file) {
      if (typeof file === "string") {
        return file; // Already a URL
      }
      return URL.createObjectURL(file);
    },

    async loadReviewData() {
      if (this.isEditing) {
        try {
          await this.fetchReviewById(this.id);
          const review = this.currentReview;

          if (review) {
            this.form = {
              reviewerName: review.reviewerName,
              reviewText: review.reviewText,
              rating: review.rating || 5,
              images: [],
              video: null,
            };

            // Set existing files for preview
            this.existingImages = review.images || [];
            this.existingVideo = review.video || null;
          }
        } catch (error) {
          console.error("Error loading review:", error);
          this.$router.push("/admin/reviews");
        }
      }
    },
  },
  async created() {
    await this.loadReviewData();
  },
  beforeUnmount() {
    this.clearCurrentReview();
  },
};
</script>

<style scoped>
.review-form-page {
  animation: fadeIn 0.5s ease-out;
}

.rating-input {
  display: flex;
  align-items: center;
}

.star {
  cursor: pointer;
  font-size: 1.5rem;
  color: #dee2e6;
  transition: color 0.3s ease;
  margin-right: 0.25rem;
}

.star.active,
.star.hoverable {
  color: #ffc107;
}

.star:hover {
  color: #ffc107;
}

.rating-text {
  font-weight: 600;
  color: var(--dark-color);
}

.review-preview {
  background: var(--light-color);
  padding: 1rem;
  border-radius: var(--border-radius);
  border: 1px solid #dee2e6;
}

.review-preview .rating i {
  font-size: 0.9rem;
  margin-right: 0.1rem;
}

.review-text {
  line-height: 1.5;
  color: var(--dark-color);
  font-size: 0.95rem;
  /* Ensure emojis render correctly */
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", "Android Emoji", "EmojiSymbols", "EmojiOne Mozilla", "Twemoji Mozilla", "Segoe UI", "Helvetica Neue", Arial, sans-serif;
  font-feature-settings: "liga" off;
  -webkit-font-feature-settings: "liga" off;
  -moz-font-feature-settings: "liga" off;
  text-rendering: optimizeLegibility;
}

/* Ensure textarea supports emojis when typing */
#reviewText.form-control {
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", "Android Emoji", "EmojiSymbols", "EmojiOne Mozilla", "Twemoji Mozilla", "Segoe UI", "Helvetica Neue", Arial, sans-serif;
  font-feature-settings: "liga" off;
  -webkit-font-feature-settings: "liga" off;
  -moz-font-feature-settings: "liga" off;
  text-rendering: optimizeLegibility;
}

.media-preview .media-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-top: 1rem;
}

.media-item,
.media-more {
  aspect-ratio: 1;
  border-radius: 4px;
  overflow: hidden;
}

.media-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.media-more {
  background: rgba(0, 0, 0, 0.7);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
}

.video-indicator {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
}

.media-summary {
  text-align: center;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  border-left: 3px solid var(--primary-color);
}

@media (max-width: 768px) {
  .review-form-page .col-lg-4 {
    margin-top: 1rem;
  }

  .media-preview .media-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
